import { Button, Checkbox, Icon, Modal, ModalBody, ModalFooter, ModalHeader } from '@yembo/yemblocks-core';
import { useState } from 'react';
import { Room, SnapshotImage, Video } from 'src/types';
import { generateKey } from 'src/util/generateKey';
import { UploadImagesButton } from '../UploadImagesButton';

export type SelectedImage = {
  key: string;
  file?: File;
  importedUrl?: string | null;
};

type Props = {
  isOpen: boolean;
  selectedImages: SelectedImage[];
  uploadedImages: SelectedImage[];
  images: SnapshotImage[];
  videos: Video[];
  rooms: Room[];
  onSelectImage: (selectedImages: SelectedImage[]) => void;
  onUploadImage: (uploadedImages: SelectedImage[]) => void;
  onClose: () => void;
};

export const ImageSelector = ({
  isOpen,
  selectedImages,
  uploadedImages,
  images,
  videos,
  rooms,
  onSelectImage,
  onUploadImage,
  onClose,
}: Props): React.JSX.Element => {
  const [isImageLoading, setIsImageLoading] = useState(false);

  const onCheck = ({ key, importedUrl, file }: SelectedImage, isChecked: boolean) => {
    const updatedImages = isChecked
      ? selectedImages.filter((image) => image.key !== key)
      : [...selectedImages, { key, importedUrl, file }];
    onSelectImage(updatedImages);
  };

  const onImageUpload = (file: File) => {
    const uploadedImage = {
      key: generateKey({ type: 'image' }),
      file,
      importedUrl: URL.createObjectURL(file),
    };

    onUploadImage([...uploadedImages, uploadedImage]);
    onSelectImage([...selectedImages, uploadedImage]);
  };

  const renderImagesSection = (images: SelectedImage[]) => {
    const imageSection = images.map((image) => {
      const isChecked = selectedImages.some(({ key }) => image.key === key);
      return (
        <div key={image.key} className='image-item' onClick={() => onCheck(image, isChecked)}>
          <img className='uploaded-image' src={image.importedUrl ?? undefined} />
          <Checkbox className='uploaded-image-checkbox' isChecked={isChecked} readOnly />
        </div>
      );
    });
    return imageSection;
  };

  const onCloseModal = () => {
    onUploadImage([]);
    onSelectImage([]);
    onClose();
  };

  const renderRoomsImages = () => {
    const imageListByRooms = videos.map((video) => {
      const imageList = images.filter((image) => image.videoKey === video.key);
      // Removing duplicate image objects
      const uniqueImageList = imageList.filter(
        (image, index, self) => index === self.findIndex((img) => img.key === image.key)
      );
      const room = rooms.filter((room) => room.key === video.roomKey);
      return {
        key: room[0]?.key,
        name: room[0]?.name,
        images: uniqueImageList,
      };
    });

    return imageListByRooms.map((room) => {
      if (room.images.length === 0 || !room.images.some(({ importedUrl }) => importedUrl)) {
        return null;
      }

      return (
        <div key={room.key} className='room-images-container'>
          <p className='room-title'>{room.name}</p>
          <div>{renderImagesSection(room.images)}</div>
        </div>
      );
    });
  };

  return (
    <>
      {isOpen && (
        <Modal className='chat-images-modal' onClose={onCloseModal}>
          <ModalHeader headerText='Select Images'>Click below to upload Images from your computer</ModalHeader>
          <ModalBody>
            <>
              <div className='action-buttons-container'>
                <UploadImagesButton onUpload={onImageUpload} setIsImageLoading={setIsImageLoading} />
                <Button
                  kind='flat'
                  text='Undo Selections'
                  icon={<Icon name='reset' />}
                  onClick={() => onSelectImage([])}
                  isDisabled={!selectedImages.length}
                />
              </div>
              {Boolean(uploadedImages.length) && (
                <div className='room-images-container'>
                  <p className='room-title'>Uploaded Images</p>
                  <div>{renderImagesSection(uploadedImages)}</div>
                </div>
              )}
              {renderRoomsImages()}
            </>
          </ModalBody>
          <ModalFooter>
            <Button text='Done' onClick={onClose} isLoading={isImageLoading} />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
